<template>
  <nav
    class="hidden lg:flex lg:gap-6 items-center justify-between py-1.5 "
    aria-label="Global"
  >
    <div class="hidden lg:flex lg:gap-x-12 items-center">
      <PopoverRoot :open="isOpenMenu" @update:open="isOpenMenu = $event">
        <PopoverTrigger as-child>
          <UButton
            variant="ghost"
            class="flex items-center gap-x-1  ring-0 focus:ring-0 focus:outline-none"
          >
            <Icon
              :name="isOpenMenu ? 'heroicons:x-mark' : 'heroicons:bars-3'"
              class="w-7 h-7 text-primary-color text-[#003a70] "
              aria-hidden="true"
            />
          </UButton>
        </PopoverTrigger>
        <PopoverPortal>
          <PopoverContent :side-offset="5">
            <div
              v-if="NavCategories"
              class="bg-white overflow-y-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 lg:px-10 xl:gap-x-8 divide-x divide-[#99999954] border-b-[10px] border-b-secondary-500 w-screen h-[calc(100vh-190px)] shadow-xl"
            >
              <div class="overflow-auto">
                <div
                  class="dj-category-title p-6 text-secondary-600 text-3xl leading-none font-bold uppercase"
                >
                  {{ t('DjHeaderBottom.products') }}
                </div>
                <ul>
                  <li
                    v-for="(item, index) in NavCategories"
                    :key="index"
                    class="cursor-pointer dj-category-label mb-3 text-base font-bold uppercase main h-12 flex items-center px-4 py-6 box-border"
                    :class="`${item?.externalId} ${selectedMainMenu?.name === item?.name ? 'active' : ''}`"
                    @click="selectMainMenu(item)"
                  >
                    <NuxtImg
                      v-if="getNavCFIcon(item)"
                      :src="getNavCFIcon(item)"
                      class="size-5 mr-2"
                    />
                    <NuxtLink class="truncate">
                      {{ item?.name }}
                    </NuxtLink>
                  </li>
                </ul>
              </div>
              <div
                v-if="selectedMainMenu && selectedMainMenu.childrenCategories.length > 0"
                class="w-full overflow-auto"
              >
                <div class="dj-category-subtitle p-4">
                  <NuxtLink :to="getMainMenuPath(selectedMainMenu.externalId)" @click="closeMenu">
                    {{ selectedMainMenu?.name }}
                  </NuxtLink>
                </div>
                <ul>
                  <li
                    v-for="(subItem, index) in selectedMainMenu.childrenCategories"
                    :key="index"
                    class="cursor-pointer dj-category-label mb-3 text-base font-bold uppercase sub h-12 flex items-center px-4 py-6 box-border sub"
                    :class="`${selectedMainMenu.externalId} ${selectedSubMenu === subItem ? 'active' : ''}`"
                    @click="selectSubMenu(subItem)"
                  >
                    <NuxtLink>
                      {{ subItem?.name }}
                    </NuxtLink>
                  </li>
                </ul>
              </div>
              <div
                v-if="selectedSubMenu && selectedSubMenu.childrenCategories.length > 0"
                class="w-full overflow-auto"
              >
                <div class="dj-category-subtitle p-4">
                  <NuxtLink
                    :to="getSubMenuPath(selectedMainMenu.externalId, selectedSubMenu.externalId)"
                    @click="closeMenu"
                  >
                    {{ selectedSubMenu?.name }}
                  </NuxtLink>
                </div>
                <ul>
                  <li
                    v-for="(subSubItem, index) in selectedSubMenu.childrenCategories"
                    :key="index"
                    class="cursor-pointer dj-category-label mb-3 text-base font-bold uppercase sub h-12 flex items-center px-4 py-6 box-border truncate"
                    :class="`${selectedMainMenu.externalId} ${selectedSubSubMenu === subSubItem ? 'active' : ''}`"
                    @click="selectSubSubMenu(subSubItem)"
                  >
                    <NuxtLink
                      :to="
                        getSubSubMenuPath(
                          selectedMainMenu.externalId,
                          selectedSubMenu.externalId,
                          subSubItem.externalId
                        )
                      "
                      class="truncate"
                      @click="closeMenu"
                    >
                      {{ subSubItem?.name }}
                    </NuxtLink>
                  </li>
                </ul>
              </div>
            </div>
          </PopoverContent>
        </PopoverPortal>
      </PopoverRoot>
    </div>
  </nav>
</template>
<script setup lang="ts">
  import { useI18n } from 'vue-i18n';
  import {
    PopoverRoot,
    PopoverTrigger,
    PopoverContent,
    PopoverPortal,
  } from 'reka-ui';
  defineOptions({
    name: 'DjHeaderBottom',
  });
  interface SelectedMenuItem {
    childrenCategories: string | any[];
    externalId: any;
    name: string;
    customFieldValues: any[];
  }
  defineProps({
    email: {
      type: String,
      default: '',
    },
    phone: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      default: '',
    },
  });
  const selectedMainMenu = ref<SelectedMenuItem | null>(null);
  const selectedSubMenu = ref<SelectedMenuItem | null>(null);
  const selectedSubSubMenu = ref<SelectedMenuItem | null>(null);
  const router = useRouter();
  const localePath = useLocalePath();
  const { t } = useI18n();
  const isOpenMenu = ref(false);
  const selectMainMenu = (item: SelectedMenuItem | null): void => {
    if (item?.childrenCategories.length === 0) {
      closeMenu();
      router.push(localePath({ path: `/c/${item.externalId}` }));
    } else {
      selectedMainMenu.value = item;
    }
    selectedSubMenu.value = null;
  };
  const selectSubMenu = (item: SelectedMenuItem | null): void => {
    if (item?.childrenCategories.length === 0) {
      closeMenu();
      router.push(localePath({ path: `/c/${item.externalId}` }));
    } else {
      selectedSubMenu.value = item;
    }
    selectedSubSubMenu.value = null;
  };
  const selectSubSubMenu = (item: SelectedMenuItem | null): void => {
    if (item?.childrenCategories.length === 0) {
      closeMenu();
      router.push(localePath({ path: `/c/${item.externalId}` }));
    } else {
      selectedSubSubMenu.value = item;
    }
  };
  const getNavCFIcon = (item: SelectedMenuItem | null): string | undefined => {
    const icon = item?.customFieldValues?.find(
      (field: any) => field.customField?.externalId === 'Picto_Nav'
    );
    return icon?.value?.value;
  };
  const { getNavigationCategories } = useNavCategory();
  const { locale } = useI18n();
  const { categories: NavCategories } = await getNavigationCategories(locale.value);

  function closeMenu() {
    isOpenMenu.value = false;
  }
  const getMainMenuPath = (externalId: string) => {
    return localePath(`/c/${externalId}`);
  };
  const getSubMenuPath = (mainExternalId: string, subExternalId: string) => {
    return localePath(`/c/${mainExternalId}/${subExternalId}`);
  };
  const getSubSubMenuPath = (
    mainExternalId: string,
    subExternalId: string,
    subSubExternalId: string
  ) => {
    return localePath(`/c/${mainExternalId}/${subExternalId}/${subSubExternalId}`);
  };
</script>
<style lang="scss" scoped>
  @use './DjHeaderBottom.scss';
</style>
